import axios from '../utils/axiosConfig';

const adminAPI = {
  // =================== USER MANAGEMENT ===================
  getUsers: async (params = {}) => {
    const response = await axios.get('/admin/users', { params });
    return response.data;
  },

  getUserDetails: async (userId) => {
    const response = await axios.get(`/admin/users/${userId}`);
    return response.data;
  },

  updateUserRole: async (userId, newRole) => {
    const response = await axios.put(`/admin/users/${userId}/role`, { newRole });
    return response.data;
  },

  banUser: async (userId, reason) => {
    const response = await axios.post(`/admin/users/${userId}/ban`, { reason });
    return response.data;
  },

  unbanUser: async (userId) => {
    const response = await axios.post(`/admin/users/${userId}/unban`);
    return response.data;
  },

  // Get user details
  getUserDetails: async (userId) => {
    const response = await axios.get(`/admin/users/${userId}`);
    return response.data;
  },

  // Update user
  updateUser: async (userId, userData) => {
    const response = await axios.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  // Suspend user
  suspendUser: async (userId, reason, days = 7) => {
    const response = await axios.post(`/admin/users/${userId}/suspend`, { 
      reason, 
      days 
    });
    return response.data;
  },

  // Unsuspend user
  unsuspendUser: async (userId) => {
    const response = await axios.post(`/admin/users/${userId}/unsuspend`);
    return response.data;
  },

  // =================== TRANSACTION MANAGEMENT ===================
  getTransactions: async (params = {}) => {
    const response = await axios.get('/admin/transactions', { params });
    return response.data;
  },

  getTransactionDetails: async (transactionId) => {
    const response = await axios.get(`/admin/transactions/${transactionId}`);
    return response.data;
  },

  retryTransaction: async (transactionId) => {
    const response = await axios.post(`/admin/transactions/${transactionId}/retry`);
    return response.data;
  },

  refundTransaction: async (transactionId, reason) => {
    const response = await axios.post(`/admin/transactions/${transactionId}/refund`, { reason });
    return response.data;
  },

  // =================== CONTENT MODERATION ===================
  getFlaggedContent: async (params = {}) => {
    const response = await axios.get('/admin/moderation/flagged-content', { params });
    return response.data;
  },

  resolveReport: async (reportId, action, resolution) => {
    const response = await axios.post(`/admin/moderation/reports/${reportId}/resolve`, {
      action,
      resolution,
    });
    return response.data;
  },

  removeContent: async (contentType, contentId, reason) => {
    const response = await axios.post(`/admin/moderation/${contentType}/${contentId}/remove`, {
      reason,
    });
    return response.data;
  },

  // =================== ANALYTICS ===================
  getDashboardStats: async (params = {}) => {
    const response = await axios.get('/admin/analytics/dashboard', { params });
    return response.data;
  },

  // =================== ADMIN ACTIONS LOG ===================
  getAdminActions: async (params = {}) => {
    const response = await axios.get('/admin/actions', { params });
    return response.data;
  },

  // =================== VTU PRODUCT MANAGEMENT ===================
  getVtuProducts: async (params = {}) => {
    const response = await axios.get('/admin/vtu/products', { params });
    return response.data;
  },

  updateVtuProductCommission: async (productId, commission) => {
    const response = await axios.put(`/admin/vtu/products/${productId}/commission`, {
      commission,
    });
    return response.data;
  },

  syncVtuProducts: async () => {
    const response = await axios.post('/admin/vtu/products/sync');
    return response.data;
  },

  // =================== DATA PROVIDER CONFIG ===================
  getDataProviderConfig: async () => {
    const response = await axios.get('/admin/vtu/data-provider-config');
    return response.data;
  },

  updateDataProviderConfig: async (network, providerType) => {
    const response = await axios.put(
      `/admin/vtu/data-provider-config/${network}`,
      { providerType }
    );
    return response.data;
  },

  // =================== AIRTIME PROVIDER CONFIG ===================
  getAirtimeProviderConfig: async () => {
    const response = await axios.get('/admin/vtu/airtime-provider-config');
    return response.data;
  },

  updateAirtimeProviderConfig: async (providerType) => {
    const response = await axios.put('/admin/vtu/airtime-provider-config', {
      providerType,
    });
    return response.data;
  },

  // =================== MARKETPLACE BUSINESS MANAGEMENT ===================
  getAllBusinesses: async (params = {}) => {
    const response = await axios.get('/admin/marketplace/businesses', { params });
    return response.data;
  },

  getBusinessDetailsAdmin: async (businessId) => {
    const response = await axios.get(`/admin/marketplace/businesses/${businessId}`);
    return response.data;
  },

  updateBusinessAdmin: async (businessId, data) => {
    const response = await axios.put(`/admin/marketplace/businesses/${businessId}`, data);
    return response.data;
  },

  deleteBusinessAdmin: async (businessId) => {
    const response = await axios.delete(`/admin/marketplace/businesses/${businessId}`);
    return response.data;
  },

  verifyBusiness: async (businessId, verified) => {
    const response = await axios.patch(`/admin/marketplace/businesses/${businessId}/verify`, { verified });
    return response.data;
  },

  // =================== MARKETPLACE REVIEWS ===================
  getAllReviews: async (params = {}) => {
    const response = await axios.get('/admin/marketplace/reviews', { params });
    return response.data;
  },

  moderateReview: async (reviewId, status, moderationNotes = null) => {
    const response = await axios.patch(`/admin/marketplace/reviews/${reviewId}/moderate`, {
      status,
      moderationNotes,
    });
    return response.data;
  },

  // =================== MARKETPLACE ORDERS ===================
  getAllOrders: async (params = {}) => {
    const response = await axios.get('/admin/marketplace/orders', { params });
    return response.data;
  },

  getOrderDetailsAdmin: async (orderId) => {
    const response = await axios.get(`/admin/marketplace/orders/${orderId}`);
    return response.data;
  },

  updateOrderAdmin: async (orderId, data) => {
    const response = await axios.patch(`/admin/marketplace/orders/${orderId}`, data);
    return response.data;
  },

  // =================== ANNOUNCEMENT MANAGEMENT ===================
getAllAnnouncements: async (params = {}) => {
  const response = await axios.get('/announcements/admin', { params });
  return response.data;
},

createAnnouncement: async (data) => {
  const response = await axios.post('/announcements', data);
  return response.data;
},

updateAnnouncement: async (id, data) => {
  const response = await axios.put(`/announcements/${id}`, data);
  return response.data;
},

deleteAnnouncement: async (id) => {
  const response = await axios.delete(`/announcements/${id}`);
  return response.data;
},

// =================== PROMOTION MANAGEMENT ===================
  getAllPromotions: async (params = {}) => {
    const response = await axios.get('/announcements/promotions/admin', { params });
    return response.data;
  },

  createPromotion: async (data) => {
    const response = await axios.post('/announcements/promotions', data);
    return response.data;
  },

  updatePromotion: async (id, data) => {
    const response = await axios.put(`/announcements/promotions/${id}`, data);
    return response.data;
  },

  deletePromotion: async (id) => {
    const response = await axios.delete(`/announcements/promotions/${id}`);
    return response.data;
  },

  getWalletTransactions: async (params = {}) => {
    const response = await axios.get('/admin/transactions/wallet', { params });
    return response.data;
  },
};

export default adminAPI;